import React, { Component, lazy, Suspense } from "react";
import { Routes as RouterRoutes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import RootRedirect from "components/RootRedirect";
import {
  GetOAuthToken,
  IsValidLoginAuth,
  IsValidLPROIAuth,
  IsValidSemiAuth,
  IsValidSessionSwitch,
} from "common/storage";
import { withRouter } from "common/utils/router.util";
import { LOG_MODULES, trackException, getAuthStorageDiagnostics } from "common/errorTracker";
/**
 * General Website Routes (Non-Admin)
 * Description:- Routes that visible in registration, login related, website layout routes, general app required routes.
 */
import SignIn from "views/SignIn";
import ForgotPassword from "views/ForgotPassword";
import ResetPassword from "views/ResetPassword";
import Home from "views/Home";
import NotFound from "views/NotFound";
import Logout from "views/Logout";

// <!--Registration Routes + Validation Screen Routes -->
import TokenValidator from "views/TokenValidator";

// import AgentDetails from "views/AgentDetails";
import RegisterAgent from "views/RegisterAgent";
// import NewRegisterAgent from "views/NewRegisterAgent";

// <!-- PMA ROUTES -->
//Note:- Enabled lazy load. Reason is large size.
//import connectedAgentPMA from 'views/Pma';
import SubdivisionHacienda from "views/Pma/PmaEditor/SubdivisionHacienda";
import SubdivisionDelMesa from "views/Pma/PmaEditor/SubdivisionDelMesa";
import PMA from "views/Pma/PmaEditor";
import MailProMain from "views/Pma/MailPro";
import NewAgentStart from "views/NewAgentStart";
import DataVerification from "views/Pma/DataVerification/AgentDataVerification";
import DataVerificationTest from "views/Pma/DataVerification";
//import SendToPrint from 'views/Pma/SendToPrint';
///import TestPrint from 'views/Pma/TestPrint';
// import PrintPMA from "views/Pma/PmaEditor/PrintPMA";
import YearEndPrint from "views/Pma/PmaEditor/YearEndPrint";
import PrintSpeedometerPMA from "views/Pma/PmaEditor/PrintSpeedometerPMA";
import PrintYearEndPMA from "views/Pma/PmaEditor/PrintYearEndPMA";
import PrintMarketTrendsPMA from "views/Pma/PmaEditor/PrintMarketTrendsPMA";
import PrintTableQuadrantsPMA from "views/Pma/PmaEditor/PrintTableQuadrants";
import PrintBubbleTablePMA from "views/Pma/PmaEditor/PrintBubbleTable";
import PrintBubbleCompare from "views/Pma/PmaEditor/PrintBubbleCompare";
import PrintMarketIndicators from "views/Pma/PmaEditor/PrintMarketIndicators";
/**
 * (Agent + Admin) Admin Account Routes
 * Note:- Screens that both agent and adminc an see.
 */
import Dashboard from "views/Dashboard";
import Account from "views/Account";
import Schedule from "views/Schedule";
import CreditCardManager from "views/CreditCardManager";
import PmaInvoice from "views/PmaInvoice";
//import PrintSchedule from 'views/Schedule/PrintSchedule'; RETIRED
import AgentStrengthsScreen from "views/PmaAdmin/AgentStrengths";
// import AgentAdvantagesScreen from "views/PmaAdmin/AgentAdvantages";
import AgentMissionStatementsScreen from "views/PmaAdmin/AgentMissions";
import PropertyHighlightsScreen from "views/PmaAdmin/PropertyHighlights";
import QuadrantEditor from "views/PmaAdmin/QuadrantEditor";
import AgentFocus from "views/PmaAdmin/AgentFocus";
import ScheduleDemo from "views/NewAgentStart/ScheduleDemo";
/**
 * Pure Admin Account Routes
 * Admin only. Only use lazyload.
 */
/**
 * IF YOUR COMPONENT USING CONTEXT STORE THEN DONT USE LAZY LOADING WITH THEM.
 */
import CreateAgent from "views/CreateAgent";
import { isUserAdmin, isUserAgent, isUserBrokerAdmin } from "common/roles/role";
import { canTokenAccessAdminPath, isSessionSwitchPathBlocked, getOAuthData } from "common/roles/adminAccess";

// Loading component for lazy loading
const LoadingComponent = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '200px',
    fontSize: '18px'
  }}>
    Loading...
  </div>
);

// Redirect to external website based on path param
const WebsiteRedirect = () => {
  const { website } = useParams();
  React.useEffect(() => {
    if (website) {
      window.location = website;
    }
  }, [website]);
  return (
    <div style={{ padding: '20px' }}>Redirecting...</div>
  );
};

// Simple test component to debug routing
const TestComponent = () => (
  <div style={{ 
    padding: '20px', 
    backgroundColor: '#f0f0f0', 
    border: '2px solid #333',
    margin: '20px'
  }}>
    <h1>Test Component Loaded Successfully!</h1>
    <p>If you can see this, routing is working correctly.</p>
    <p>Current time: {new Date().toLocaleString()}</p>
  </div>
);

// Helper function to render component with or without Suspense
const renderComponent = (Component, props) => {
  // For core components (Home, SignIn, Dashboard), don't wrap in Suspense
  const coreComponents = ['Home', 'SignIn', 'Dashboard', 'Account', 'Schedule'];
  const isCoreComponent = Component && Component.name && coreComponents.includes(Component.name);
  
  if (isCoreComponent) {
    return <Component {...props} />;
  }
  
  return (
    <Suspense fallback={<LoadingComponent />}>
      <Component {...props} />
    </Suspense>
  );
};

// Blocks admin-only screens for restricted admins (see common/roles/adminAccess).
// Uses the router location so it checks the page actually being opened.
const RestrictedAdminGate = ({ enabled, children }) => {
  const { pathname } = useLocation();
  if (
    (enabled && !canTokenAccessAdminPath(getOAuthData(), pathname)) ||
    isSessionSwitchPathBlocked(pathname)
  ) {
    return <Navigate to={"/dashboard?mode=1"} replace />;
  }
  return children;
};

// Helper function to create private route element
const createPrivateRouteElement = (Component, requireSemiAuth, requireFullAuth, isaAgentScreen, isaAdminScreen, isABrokerAdminScreen) => {
  const authed = authMiddleware(true, requireSemiAuth, requireFullAuth);
  const isValidSessionSwitch = IsValidSessionSwitch();
  const oauthToken = GetOAuthToken();
  const oauthData = oauthToken ? JSON.parse(oauthToken) : null;

  if (authed === true) {
    if (
      (isValidSessionSwitch && isaAdminScreen && !isaAgentScreen) ||
      (!isValidSessionSwitch &&
        oauthData &&
        isUserAdmin(oauthData.roleID) &&
        !isaAdminScreen &&
        (isaAgentScreen || isABrokerAdminScreen)) ||
      (!isValidSessionSwitch &&
        oauthData &&
        isUserAgent(oauthData.roleID) &&
        (isaAdminScreen || isABrokerAdminScreen) &&
        !isaAgentScreen) ||
      (!isValidSessionSwitch &&
        oauthData &&
        isUserBrokerAdmin(oauthData.roleID) &&
        (isaAdminScreen || isaAgentScreen) &&
        !isABrokerAdminScreen)
    ) {
      //Its important for session switch to redirect to dashboard page
      return <Navigate to={"/dashboard?mode=1"} replace />;
    } else {
      //You dont have access to requested page because your access role has changed. If it's a admin session redirect to mailpro insight page otherwise dashboard page with access message
      return (
        <RestrictedAdminGate
          enabled={!isValidSessionSwitch && isaAdminScreen && !isaAgentScreen}
        >
          <Component />
        </RestrictedAdminGate>
      );
    }
  } else {
    //a partial session here (token present but login invalid) means the user
    //thinks they're signed in but is getting kicked back — log which page and why
    try {
      const storageDiagnostics = getAuthStorageDiagnostics();
      if (
        storageDiagnostics.hasOAuthToken ||
        storageDiagnostics.stateIsUserLoggedIn
      ) {
        trackException({
          module: LOG_MODULES.AUTH_GUARD,
          code: 'BOUNCED_TO_LOGIN_PARTIAL_SESSION',
          error: new Error(
            `Route guard bounced a partial session to login from ${window.location.pathname}`
          ),
          level: 'warning',
          data: {
            pathname: window.location.pathname,
            storageDiagnostics,
          },
        });
      }
    } catch {
      // Logging must never break routing
    }
    return <Navigate to={"/login?mode=3"} replace />;
  }
};

// Lazy loaded components
const OBLandingPage = lazy(() => import("views/OBLandingPage"));
const Legal = lazy(() => import("views/Legal"));
const PrintPMA = lazy(() => import("views/Pma/PmaEditor/PrintPMA"));
const AgentYearEndStatement = lazy(() => import("views/PmaAdmin/AgentYearEndStatement"));
const AgentInvoice = lazy(() => import("views/PmaAdmin/AgentInvoice"));
const ClientTestimonials = lazy(() => import("views/PmaAdmin/ClientTestimonials"));
const AgentMissions = lazy(() => import("views/PmaAdmin/AgentMissions"));
const AgentROIForm = lazy(() => import("views/PmaAdmin/AgentROIForm"));
const AgentPerformance = lazy(() => import("views/PmaAdmin/AgentPerformance"));
const AgentDetails = lazy(() => import("views/AgentDetails"));
const CreditCardManagerLazy = lazy(() => import("views/CreditCardManager"));
const SpecialPayment = lazy(() => import("views/SpecialPayment"));
const SpecialPaymentV1 = lazy(() => import("views/SpecialPaymentV1"));
const InvoiceHistory = lazy(() => import("views/InvoiceHistory"));
const SearchAgent = lazy(() => import("views/SearchAgent"));
const MLSAgentsAdmin = lazy(() => import("views/MLSAgentsAdmin"));
const PrintManager = lazy(() => import("views/PrintManager"));
const AgentPmaSchedulesV1 = lazy(() => import("views/AgentPmaSchedulesV1"));
const ScheduleAdminV2 = lazy(() => import("views/Schedule/admin/indexV2"));
const MailproInsightsV1 = lazy(() => import("views/MailproInsightsV1"));
const LgConstants = lazy(() => import("views/LgConstants"));
const SalesReportsAgentPerformance = lazy(() => import("views/SalesReports/AgentPerformance"));
const HelpRequest = lazy(() => import("views/HelpRequest"));
const OBAgentInvite = lazy(() => import("views/AgentOnbordingPortal/OBAgentInvite"));
const OBAgentROIInvite = lazy(() => import("views/AgentOnbordingPortal/OBAgentROIInvite"));
const OBAgentPresentations = lazy(() => import("views/AgentOnbordingPortal/OBAgentPresentations"));
const OBViewBrokerages = lazy(() => import("views/AgentOnbordingPortal/OBViewBrokerages"));
const AreaROIPreview = lazy(() => import("views/AgentROI/AreaROIPreview"));
const AgentROI = lazy(() => import("views/AgentROI"));
const CompassRevShareReport = lazy(() => import("views/CompassRevShareReport"));
const PMAUrlSwitcher = lazy(() => import("views/PMAUrlSwitcher"));

const LegalRouteScreen = () => (
  <Suspense fallback={<LoadingComponent />}>
    <Legal />
  </Suspense>
);

const authMiddleware = (checkAuth, requireSemiAuth, requireFullAuth) => {
  // if (window.location.pathname !== '/login') {

  // };
  let isValidLogin = IsValidLoginAuth();
  let isValidSemi = IsValidSemiAuth();
  if (requireSemiAuth && isValidSemi) {
    return true;
  } else if (requireFullAuth && isValidLogin) {
    return true;
  } else if (requireSemiAuth && isValidLogin) {
    return true;
  } else {
    return false;
  }
};

const oneWayAuth = () => {
  let isValidLogin = IsValidLoginAuth();
  let isValidSemi = IsValidSemiAuth();
  if (isValidSemi || isValidLogin) {
    return true;
  }
  return false;
};

/**
 * Private(Auth) routes
 * isaAgentScreen :- If screen can be only use by agnet access/ by agent
 * isaAdminScreen :- If screen can be only use by admin access/ by admin
 */

// PrivateRoute component removed - now using createPrivateRouteElement helper function

// AuthRedirectRoute and LandingPageRedirectRoute components removed - now using inline element logic

class AppRoutes extends Component {
  componentDidMount() {}

  render() {
    return (
      <RouterRoutes>
        {/* Test route to see if basic routing works - MUST BE FIRST */}
        <Route
          path="/test"
          element={<TestComponent />}
        />
        {/* Auth routes */}
        <Route 
          path="/login" 
          element={
            (() => {
              let isValid = oneWayAuth();
              return isValid === false ? <SignIn /> : <Navigate to="/" replace />;
            })()
          }
        />
        <Route 
          path="/password/forgot"
          element={
            (() => {
              let isValid = oneWayAuth();
              return isValid === false ? <ForgotPassword /> : <Navigate to="/" replace />;
            })()
          } 
        />
        <Route 
          path="/password/reset"
          element={
            (() => {
              let isValid = oneWayAuth();
              return isValid === false ? <ResetPassword /> : <Navigate to="/" replace />;
            })()
          } 
        />
        {/* Other specific routes */}
        <Route
          path="/agent/open/website/:website"
          element={<WebsiteRedirect />}
        />
        {/* <PrivateRoute requireSemiAuth={false} requireFullAuth={true} isaAgentScreen={true} isaAdminScreen={true} path="/session/switch" element={<SessionSwitch />} /> */}
        <Route
          path="/home"
          element={createPrivateRouteElement(Home, true, false, true, true, true)}
        />
        {/* <Route
          path="/about"
          element={createPrivateRouteElement(About, true, false, true, true)}
        /> */}
        {/* <PrivateRoute
          requireSemiAuth={true}
          requireFullAuth={false}
          isaAgentScreen={true}
          isaAdminScreen={true}
          isaBrokerAdminScreen={false}
          path="/help"
          component={Help}
        /> */}
        <Route
          path="/legal"
          element={createPrivateRouteElement(LegalRouteScreen, true, false, true, true)}
        />
        <Route
          path="/schedule"
          element={createPrivateRouteElement(Schedule, false, true, true, true)}
        />
        <Route path="/404" element={<NotFound />} />
        <Route path="/logout" element={<Logout />} />
        {/*  */}
        {/* Register Agent Routes + Validation Screen Routes + Sales + Onboarding landing page routes */}
        {/*  */}
        <Route path="/validateAgentToken" element={<TokenValidator />} />
        <Route path="/start" element={<NewAgentStart />} />
        <Route
          path="/agent/register"
          element={createPrivateRouteElement(RegisterAgent, true, false, true, false)}
        />
        {/* <Route
          path="/agent/new-register"
          element={createPrivateRouteElement(NewRegisterAgent, false, false, false, false)}
        /> */}
        {/* this would be a part of lp roi session */}
        {/* <LandingPageRedirectRoute
          //requireSemiAuth={false}
          //requireFullAuth={false}
          //element={<SalesLandingPage />}
          component={OBLandingPage}
          path="/lp/agent/welcomepage"
        /> */}
        <Route
          //requireSemiAuth={false}
          //requireFullAuth={false}
          //element={<SalesLandingPage />}
          path="/lp/schedule/presentation"
          element={
            <Suspense fallback={<LoadingComponent />}>
              <OBLandingPage />
            </Suspense>
          }
        />
        {/*  */}
        {/* //
        // PMA Routes
        // */}
        {/*  */}
        <Route
          path="/printpma/:listgenUserID/:mailproid"
          element={
            <Suspense fallback={<LoadingComponent />}>
              <PrintPMA />
            </Suspense>
          }
        />
        <Route
          path="/pdfprintversion/:listgenUserID/:mailproid"
          element={
            <Suspense fallback={<LoadingComponent />}>
              <PrintPMA />
            </Suspense>
          }
        />
        <Route
          path="/agent/pma/preview/:listgenUserID/:mailproid"
          element={createPrivateRouteElement(PrintPMA, false, true, true, false)}
        />
        <Route
          path="/schedule-demo"
          element={<ScheduleDemo />}
        />
        <Route
          path="/printpma/speedometer/:brokerage/:listgenUserID/:mailproid"
          element={
            <Suspense fallback={<LoadingComponent />}>
              <PrintSpeedometerPMA />
            </Suspense>
          }
        />
        <Route
          path="/printpma/year-end/:brokerage/:listgenUserID/:mailproid"
          element={
            <Suspense fallback={<LoadingComponent />}>
              <YearEndPrint />
            </Suspense>
          }
        />
        <Route
          path="/newpma/:listgenUserID/year-end/:mailproid"
          element={
            <Suspense fallback={<LoadingComponent />}>
              <PrintYearEndPMA />
            </Suspense>
          }
        />
        <Route
          path="/printpma/market-trends/:brokerage/:listgenUserID/:mailproid"
          element={
            <Suspense fallback={<LoadingComponent />}>
              <PrintMarketTrendsPMA />
            </Suspense>
          }
        />
        <Route
          path="/printpma/market-indicators/:brokerage/:listgenUserID/:mailproid"
          element={
            <Suspense fallback={<LoadingComponent />}>
              <PrintMarketIndicators />
            </Suspense>
          }
        />
        <Route
          path="/printpma/table-quadrants/:brokerage/:listgenUserID/:mailproid"
          element={
            <Suspense fallback={<LoadingComponent />}>
              <PrintTableQuadrantsPMA />
            </Suspense>
          }
        />
        <Route
          path="/printpma/bubble-table/:brokerage/:listgenUserID/:mailproid"
          element={
            <Suspense fallback={<LoadingComponent />}>
              <PrintBubbleTablePMA />
            </Suspense>
          }
        />
        <Route
          path="/printpma/bubble-compare/:brokerage/:listgenUserID/:mailproid"
          element={
            <Suspense fallback={<LoadingComponent />}>
              <PrintBubbleCompare />
            </Suspense>
          }
        />
        {/* <Route
          component={PrintPMA}
          //element={<PrintPMA />}
          path="/printpma/:version/:listgenUserID/:mailproid"
        /> */}
        {/* <Route
        component={PrintSchedule}
        path="/printschedule" /> */}
        <Route
          path="/pma/subdivision/hacienda/:subName/:areaID"
          element={createPrivateRouteElement(SubdivisionHacienda, false, true, true, false)}
        />
        <Route
          path="/pma/subdivision/delmesa/:subName/:areaID"
          element={createPrivateRouteElement(SubdivisionDelMesa, false, true, true, false)}
        />
        <Route
          path="/pma/:areaID"
          element={createPrivateRouteElement(PMA, false, true, true, false)}
        />
        <Route
          path="/pma"
          element={createPrivateRouteElement(PMA, false, true, true, false)}
        />
        <Route
          path="/mailpro/:areaID"
          element={createPrivateRouteElement(MailProMain, false, true, true, false)}
        />
        {/*<PrivateRoute
          requireSemiAuth={false}
          requireFullAuth={true}
          path="/mailpro"
          component={MailProMain}
          isaAgentScreen={true}
          isaAdminScreen={false}
          isaBrokerAdminScreen={false}
          //element={<PrintPMA />}
        />*/}
        <Route
          path="/data-verification/:cityID/:areaID"
          element={createPrivateRouteElement(DataVerification, false, true, true, false)}
        />
        <Route
          path="/test-data-verification/:cityID/:areaID"
          element={createPrivateRouteElement(DataVerificationTest, false, true, true, false)}
        />
        {/* <PrivateRoute
          requireSemiAuth={true}
          requireFullAuth={false}
          path="/pma/:isEditorMode"
          component={PrintPMA}
          isAgentOnly={true}
          isaAgentScreen={true}
          isaAdminScreen={false}
          isaBrokerAdminScreen={false}
        /> */}
        {/* <PrivateRoute
          requireSemiAuth={true}
          requireFullAuth={false}
          path="/send-to-print"
          component={PrintPMA}
          isAgentOnly={true}
          isaAgentScreen={true}
          isaAdminScreen={false}
          isaBrokerAdminScreen={false}
        /> */}
        <Route
          path="/invoice"
          element={createPrivateRouteElement(PmaInvoice, false, true, true, false)}
        />
        <Route
          path="/agent/mystrengths"
          element={createPrivateRouteElement(AgentStrengthsScreen, false, true, true, false)}
        />
        <Route
          path="/agent/property-highlights/:areaID"
          element={createPrivateRouteElement(PropertyHighlightsScreen, false, true, true, false)}
        />
        <Route
          path="/agent/property-highlights"
          element={createPrivateRouteElement(PropertyHighlightsScreen, false, true, true, false)}
        />
        <Route
          path="/agent/quadrant-editor/:areaID/:type/:position"
          element={createPrivateRouteElement(QuadrantEditor, false, true, true, false)}
        />
        <Route
          path="/agent-focus"
          element={createPrivateRouteElement(AgentFocus, false, true, true, false)}
        />
        <Route
          path="/agent/year-end-statement/:listgenUserID"
          element={createPrivateRouteElement(AgentYearEndStatement, false, true, true, false)}
        />
        <Route
          path="/agent/view-monthly-invoice/:listgenUserID"
          element={createPrivateRouteElement(AgentInvoice, false, true, true, false)}
        />
        {/* <PrivateRoute
          requireSemiAuth={false}
          requireFullAuth={true}
          component={AgentAdvantagesScreen}
          path="/agent/myadvantages"
          isaAgentScreen={true}
          isaAdminScreen={false}
          isaBrokerAdminScreen={false}
        /> */}
        <Route
          path="/agent/clienttestimonials"
          element={createPrivateRouteElement(ClientTestimonials, false, true, true, false)}
        />
        <Route
          path="/agent/missionstatements"
          element={createPrivateRouteElement(AgentMissions, false, true, true, false)}
        />
        <Route
          path="/agent/missionstatements/:mlsAreaID"
          element={createPrivateRouteElement(AgentMissionStatementsScreen, false, true, true, false)}
        />
        <Route
          path="/agent/roi/form"
          element={createPrivateRouteElement(AgentROIForm, false, true, true, false)}
        />
        <Route
          path="/agent/agent-performance"
          element={createPrivateRouteElement(AgentPerformance, false, true, true, false)}
        />
        {/*  */}
        {/* Both Admin + Agent + Other  routes */}
        {/*  */}
        <Route
          path="/dashboard"
          element={createPrivateRouteElement(Dashboard, false, true, true, true, true)}
        />
        <Route
          path="/account"
          element={createPrivateRouteElement(Account, false, true, true, true, true)}
        />
        {/* For agent, not required for the county admin */}
        <Route
          path="/agent/details/ag/:listgenUserID"
          element={createPrivateRouteElement(AgentDetails, false, true, true, false)}
        />
        {/* For Admin,not required for the county admin */}
        <Route
          path="/agent/details/ad/:listgenUserID"
          element={createPrivateRouteElement(AgentDetails, false, true, false, true)}
        />
        {/* For agent side. ag means agent */}
        <Route
          path="/cards/credit/ag/:listgenUserID/:agentFirstName/:agentLastName/:emailID"
          element={createPrivateRouteElement(CreditCardManager, false, true, true, false)}
        />
        {/* For Admin Side. ad means admin, not required for the county admin */}
        <Route
          path="/cards/credit/ad/:listgenUserID/:agentFirstName/:agentLastName/:emailID"
          element={createPrivateRouteElement(CreditCardManager, false, true, false, true)}
        />
        {/* //Not In Use right now */}
        {/* //No screen for agent as of now for special payments, not required for the county admin */}
        {/* <PrivateRoute
          requireSemiAuth={false}
          requireFullAuth={true}
          path="/specialpayments/ag/:listgenUserID/:agentFirstName/:agentLastName/:emailID/:ownedMLSID"
          //element={<SpecialPayment />}
          component={PrintPMA}
          isaAgentScreen={true}
          isaAdminScreen={false}
          isaBrokerAdminScreen={false}
        /> */}
        {/* For Admin, not required for the county admin */}
        <Route
          path="/specialpayments/ad/:listgenUserID/:agentFirstName/:agentLastName/:emailID/:ownedMLSID"
          element={createPrivateRouteElement(SpecialPayment, false, true, false, true)}
        />
        <Route
          path="/specialpayments/v1/ad/:listgenUserID/:agentFirstName/:agentLastName/:emailID/:ownedMLSID"
          element={createPrivateRouteElement(SpecialPaymentV1, false, true, false, true)}
        />
        {/* //Not In Use right now, for Agent,  not required for the county admin */}
        {/* <PrivateRoute
          requireSemiAuth={false}
          requireFullAuth={true}
          path="/agent/invoice/history/ag/:listgenUserID/:agentFirstName/:agentLastName/:emailID"
          component={InvoiceHistory}
          isaAgentScreen={false}
          isaAdminScreen={false}
          isaBrokerAdminScreen={false}
        /> */}
        {/* For Admin,  not required for the county admin */}
        <Route
          path="/agent/invoice/history/ad/:listgenUserID/:agentFirstName/:agentLastName/:emailID"
          element={createPrivateRouteElement(InvoiceHistory, false, true, false, true)}
        />
        {/*  */}
        {/* <!--- Pure Admin Routes --> */}
        {/*  */}
        <Route
          path="/agent/create"
          element={createPrivateRouteElement(CreateAgent, false, true, false, true)}
        />
        <Route
          path="/agent/search"
          element={createPrivateRouteElement(SearchAgent, false, true, false, true)}
        />
        <Route
          path="/manage/bridge/agents"
          element={createPrivateRouteElement(MLSAgentsAdmin, false, true, false, true)}
        />
        <Route
          path="/agent/print/manage"
          element={createPrivateRouteElement(PrintManager, false, true, false, true)}
        />
        <Route
          path="/agent/pma/schedulesv1"
          element={createPrivateRouteElement(AgentPmaSchedulesV1, false, true, false, true)}
        />
        <Route
          path="/admin/mailing/schedule"
          element={createPrivateRouteElement(ScheduleAdminV2, false, true, false, true)}
        />
        {/* <PrivateRoute
          exact
          requireSemiAuth={false}
          requireFullAuth={true}
          path="/admin/mailing/schedule"
          component={lazy(() => import("./views/Schedule/admin/indexV2"))}
          isaAgentScreen={false}
          isaAdminScreen={true}
          isaBrokerAdminScreen={false}
        /> */}
        {/* <PrivateRoute
          requireSemiAuth={false}
          requireFullAuth={true}
          path="/admin/hoasubdivion"
          //element={<HoaSubdivisonComplex />}
          component={PrintPMA}
          isaAgentScreen={false}
          isaAdminScreen={true}
          isaBrokerAdminScreen={false}
        /> */}
        <Route
          path="/agent/mailpro/insights"
          element={createPrivateRouteElement(MailproInsightsV1, false, true, false, true)}
        />
        <Route
          path="/lg/constants"
          element={createPrivateRouteElement(LgConstants, false, true, false, true)}
        />
        {/* Sales reports routes */}
        <Route
          path="/sales/report/agentperformance"
          element={createPrivateRouteElement(SalesReportsAgentPerformance, false, true, false, true)}
        />
        <Route
          path="/agent/help/request"
          element={createPrivateRouteElement(HelpRequest, false, true, false, true)}
        />
        <Route
          path="/onboarding/invite/agents"
          element={createPrivateRouteElement(OBAgentInvite, false, true, false, true)}
        />
        <Route
          path="/onboarding/roi/invite/agents"
          element={createPrivateRouteElement(OBAgentROIInvite, false, true, false, true)}
        />
        <Route
          path="/onboarding/presentation/agents"
          element={createPrivateRouteElement(OBAgentPresentations, false, true, false, true)}
        />
        <Route
          path="/onboarding/view/brokerages"
          element={createPrivateRouteElement(OBViewBrokerages, false, true, false, true)}
        />
        <Route
          path="/agent/printroi/:countyID/:countyName/:areaID"
          element={
            <Suspense fallback={<LoadingComponent />}>
              <AreaROIPreview />
            </Suspense>
          }
        />
        {/* For admin. aoi and areas-by-city require for open access also. ROI cannot be opened by a broker admin */}
        <Route
          path="/agent/roi/:areaID"
          element={createPrivateRouteElement(AreaROIPreview, false, true, true, true)}
        />
        <Route
          path="/agent/areas-by-city"
          element={createPrivateRouteElement(AgentROI, false, true, true, true)}
        />
        <Route
          path="/compass-rev-share-report"
          element={createPrivateRouteElement(CompassRevShareReport, false, true, true, true)}
        />
        {/* For Landing Page access */}
        <Route
          path="/lp/open/agent/roi/:areaID"
          element={
            (() => {
              let isValidLPROISession = IsValidLPROIAuth();
              return isValidLPROISession ? <AreaROIPreview /> : <Navigate to="/login?mode=2" replace />;
            })()
          }
        />
        <Route
          path="/lp/open/agent/areas-by-city"
          element={
            (() => {
              let isValidLPROISession = IsValidLPROIAuth();
              return isValidLPROISession ? <AgentROI /> : <Navigate to="/login?mode=2" replace />;
            })()
          }
        />
        {/* <!-- END !--> */}
        <Route
          //element={<PMAUrlSwitcher />}
          path="/pmaurlswitcher"
          element={
            <Suspense fallback={<LoadingComponent />}>
              <PMAUrlSwitcher />
            </Suspense>
          }
        />
        {/* DEFAULT ROOT → delegates to RootRedirect component */}
        <Route path="/" element={<RootRedirect />} />
        {/* <!-- General Routes --> */}
        <Route path="*" element={<Navigate to="/404" replace />} />
      </RouterRoutes>
    );
  }
}

export default AppRoutes;
